/**
 * MLEADER CONTEXT_DATA nested-tree builder — single descent (import side).
 *
 * The AcDbMLeader entity embeds a `CONTEXT_DATA` record (ezdxf MLeaderContext)
 * delimited by the structural markers `300 "CONTEXT_DATA{"` … `301 "}"`,
 * `302 "LEADER{"` … `303 "}"`, `304 "LEADER_LINE{"` … `305 "}"`. Inside that
 * record the numeric group codes (40/41/44/90/170/171/…) reuse AcDbMLeader-BODY
 * code numbers with COMPLETELY DIFFERENT semantics, so the same code maps to a
 * different typed field at each nesting depth (see `tmp_smleader_codemap.md` /
 * the ezdxf MULTILEADER reference).
 *
 * This builder consumes the MLEADER group stream ONCE during the parser's
 * group-walk (one `feed()` per group, in original order) and maintains a frame
 * stack — context → leader/node → leaderLine — pushing on each open marker and
 * popping on each close. At the current depth every non-marker group is routed
 * to the current frame's typed field per the code map; an unrecognised
 * in-context group is preserved VERBATIM (in original order) in
 * `rawContextGroups` so byte-faithfulness is never lost (ПРИОРИТЕТ #1).
 *
 * It REPLACES the three former post-hoc passes — `buildMLeaderRoots`,
 * `extractAnnotativeContexts`, `extractBlockAttributeReferences` — collapsing
 * them into this single descent:
 *   - leaderRoots: the proper hierarchy (each `LEADER{` → root/node, each
 *     `LEADER_LINE{` → line with its OWN vertices) of the FIRST CONTEXT_DATA;
 *   - contextData: the typed MLeaderContext tree (header + MTextData/BlockData
 *     INLINE by 290/296 flag + ezdxf field order, plane + attach trailer) of the
 *     FIRST CONTEXT_DATA;
 *   - annotativeContexts: one minimal per-scale snapshot per CONTEXT_DATA block
 *     (every block, including the first — the established B2 behaviour);
 *   - blockAttributeReferences: the trailing depth-0 `330/177/44/302` tuples.
 *
 * ALL marker / depth decisions route through `mleaderContextDepth.ts`
 * (`classifyMLeaderMarker`) — the single source of truth that preserves the
 * #258/#259 dual-use 304/301 handling. The builder runs its own depth counter
 * fed by that classifier, independent of (but identical to) the parser's depth
 * tracking, so it stays self-contained.
 */
import { classifyMLeaderMarker } from "./mleaderContextDepth";
import { decodeDxfUnicode } from "./dxfUnicodeEscape";
import type {
  DxfGroup,
  DxfPoint3D,
  DxfMLeaderAnnotativeContext,
  DxfMLeaderBlockAttributeReference,
  DxfMLeaderBlockData,
  DxfMLeaderBreakPoint,
  DxfMLeaderContext,
  DxfMLeaderLeaderLine,
  DxfMLeaderLeaderNode,
  DxfMLeaderLeaderRoot,
  DxfMLeaderMTextData,
} from "./types";

/** Result of the single CONTEXT_DATA descent. */
export interface MLeaderBuilderResult {
  /** True once a `300 "CONTEXT_DATA{"` open marker was seen — drives the
   * marker-less flat-vertex fallback in the parser finalize step. */
  hasContextMarkers: boolean;
  /** Typed MLeaderContext tree of the FIRST CONTEXT_DATA block (undefined when
   * no context was opened). */
  contextData?: DxfMLeaderContext;
  /** Proper leader hierarchy of the FIRST CONTEXT_DATA block. Empty when no
   * context / no leader lines were decoded — the parser then falls back to the
   * flat-vertex single root. */
  leaderRoots: DxfMLeaderLeaderRoot[];
  /** FULL typed tree of every CONTEXT_DATA block AFTER the first (annotative
   * per-scale blocks). Previously these were reduced to the minimal
   * `annotativeContexts` snapshot and never re-emitted — data loss (Codex #268).
   * Each carries its own context header + leader hierarchy so the writer can
   * re-emit one CONTEXT_DATA block per scale. Empty for the common single block. */
  secondaryContexts: Array<{
    context: DxfMLeaderContext;
    leaderRoots: DxfMLeaderLeaderRoot[];
    /** Unrecognised groups seen at THIS secondary CONTEXT_DATA's header depth
     * (or in a dropped leader/leader-line frame owned by it), kept verbatim in
     * source order so the writer re-emits them INSIDE this secondary block —
     * not leaked into the primary block (Codex #285-9). */
    rawContextGroups?: DxfGroup[];
  }>;
  /** Unrecognised in-context groups of the PRIMARY context, verbatim, in
   * original emit order. */
  rawContextGroups: DxfGroup[];
  /** One minimal snapshot per CONTEXT_DATA block (B2). */
  annotativeContexts: DxfMLeaderAnnotativeContext[];
  /** Trailing depth-0 block-attribute linkage tuples (B1). */
  blockAttributeReferences: DxfMLeaderBlockAttributeReference[];
}

type MutPoint = { x: number; y: number; z: number };

interface ContextFrame {
  kind: "context";
  ctx: DxfMLeaderContext;
  /** Accumulated points: basePoint / planeOrigin / planeXAxis / planeYAxis and
   * the inline MTextData/BlockData point fields (keyed below). */
  points: Map<string, MutPoint>;
  mtext?: DxfMLeaderMTextData;
  block?: DxfMLeaderBlockData;
  /** First CONTEXT_DATA → projected to contextData + leaderRoots. */
  isPrimary: boolean;
  /** Leader roots collected for THIS context. */
  roots: DxfMLeaderLeaderRoot[];
  /** Codex #285-9 — unrecognised groups seen at THIS context's HEADER depth
   * (plus any raw groups that fell back from a dropped leader/leader-line frame
   * owned by this context), kept in source order so the writer re-emits them in
   * the SAME CONTEXT_DATA block (primary OR secondary) rather than always in the
   * single flat primary list. */
  rawGroups: DxfGroup[];
  /** Minimal per-scale snapshot for THIS context (B2). */
  annot: DxfMLeaderAnnotativeContext;
}

interface LeaderFrame {
  kind: "leader";
  node: DxfMLeaderLeaderNode;
  /** lastLeaderPoint (10/20/30) + doglegVector (11/21/31). */
  points: Map<string, MutPoint>;
  /** LeaderData breaks — alternating 12↔13 pairs, no index. */
  breaks: DxfMLeaderBreakPoint[];
  /** Codex #268 — unrecognised groups seen at THIS LEADER depth (outside its
   * LEADER_LINE sub-frames), kept in source order so the writer re-emits them
   * inside the frame rather than flat at the end of CONTEXT_DATA. */
  rawGroups: DxfGroup[];
}

interface LeaderLineFrame {
  kind: "leaderLine";
  line: DxfMLeaderLeaderLine;
  /** LeaderLine breaks — 90 index + 11/21/31 start + 12/22/32 end. */
  breaks: DxfMLeaderBreakPoint[];
  /** Codex #268 — unrecognised groups seen at THIS LEADER_LINE depth, kept in
   * source order so the writer re-emits them inside the 304…305 frame rather
   * than flat at the end of CONTEXT_DATA. */
  rawGroups: DxfGroup[];
}

interface NestedFrame {
  kind: "nested";
}

type Frame = ContextFrame | LeaderFrame | LeaderLineFrame | NestedFrame;

function num(g: DxfGroup): number {
  return Number(g.value);
}

function int(g: DxfGroup): number {
  return Number.parseInt(g.value.trim(), 10);
}

/** DXF boolean group (1/0). Any non-"0" non-empty value is truthy. */
function bool(g: DxfGroup): boolean {
  const t = g.value.trim();
  return t !== "0" && t !== "";
}

function setAxis(
  points: Map<string, MutPoint>,
  key: string,
  axis: "x" | "y" | "z",
  value: number,
): void {
  let p = points.get(key);
  if (!p) {
    p = { x: 0, y: 0, z: 0 };
    points.set(key, p);
  }
  p[axis] = value;
}

function toPoint(
  points: Map<string, MutPoint>,
  key: string,
): DxfPoint3D | undefined {
  const p = points.get(key);
  return p ? { x: p.x, y: p.y, z: p.z } : undefined;
}

function lastBreak(
  breaks: DxfMLeaderBreakPoint[],
): DxfMLeaderBreakPoint | undefined {
  return breaks[breaks.length - 1];
}

export class MLeaderContextBuilder {
  private depth = 0;
  private stack: Frame[] = [];
  private contextCount = 0;
  private hasContext = false;
  private contextData: DxfMLeaderContext | undefined;
  private leaderRoots: DxfMLeaderLeaderRoot[] = [];
  private readonly secondaryContexts: Array<{
    context: DxfMLeaderContext;
    leaderRoots: DxfMLeaderLeaderRoot[];
    rawContextGroups?: DxfGroup[];
  }> = [];
  private readonly rawContextGroups: DxfGroup[] = [];
  private readonly annotativeContexts: DxfMLeaderAnnotativeContext[] = [];
  private readonly blockAttributeReferences: DxfMLeaderBlockAttributeReference[] =
    [];

  // Pending trailing block-attribute tuple (depth-0 330 → 177 → 44 → 302).
  private baHandle: string | null = null;
  private baIndex: number | null = null;
  private baWidth: number | null = null;
  // #432 r11 R11-2 — the tuple's 302 text, latched until the tuple FLUSHES (on the
  // next 330 or at end-of-body), so a tuple with NO trailing 302 (text='') still
  // round-trips (ezdxf `extract_block_attribs` stores per-330, independent of 302).
  private baValue: string | null = null;
  // #432 r10 R10-2 — true once the `100 AcDbMLeader` subclass marker has been
  // seen, i.e. we are in the AcDbMLeader BODY. A block-attribute tuple `330` only
  // opens AFTER the marker; the pre-subclass ENTITY-OWNER `330` (before it) must
  // NEVER latch as a pending block-attribute handle (else a later STRAY 177/44/302
  // with no real tuple would fabricate a block attribute from the owner handle).
  // Mirrors the parser-side `mleaderBodyStarted` gate.
  private bodyStarted = false;

  /** Consume one MLEADER group in original order. */
  feed(group: DxfGroup): void {
    const marker = classifyMLeaderMarker(group, this.depth);
    if (marker !== undefined) {
      if (marker.kind === "open") {
        this.openFrame(marker.scope);
        this.depth += 1;
      } else {
        this.closeFrame();
        this.depth = Math.max(0, this.depth - 1);
      }
      return;
    }

    if (this.depth === 0) {
      this.feedBody(group);
      return;
    }

    const frame = this.stack[this.stack.length - 1];
    if (frame === undefined) {
      this.pushRawContextGroup(group);
      return;
    }
    switch (frame.kind) {
      case "context":
        this.feedContext(frame, group);
        break;
      case "leader":
        this.feedLeader(frame, group);
        break;
      case "leaderLine":
        this.feedLeaderLine(frame, group);
        break;
      case "nested":
        this.pushRawContextGroup(group);
        break;
    }
  }

  /**
   * Route an unrecognised in-context group to its OWNING context frame so it
   * re-emits in the SAME CONTEXT_DATA block (primary OR secondary) on export.
   * Falls back to the builder-level primary list only when no context frame is
   * on the stack (malformed input). Codex #285-9 — previously every raw group
   * went to the single flat list, so a group inside a SECONDARY CONTEXT_DATA
   * leaked into the primary block (or was dropped from the secondary).
   */
  private pushRawContextGroup(g: DxfGroup): void {
    const ctx = this.nearestContext();
    if (ctx) ctx.rawGroups.push(g);
    else this.rawContextGroups.push(g);
  }

  finalize(): MLeaderBuilderResult {
    // #432 r11 R11-2 — flush the LAST pending block-attribute tuple (a body tuple
    // with no trailing 302, or the final tuple of the list), matching ezdxf
    // `extract_block_attribs`' end-of-list store.
    this.flushBlockAttr();
    // Drain any frames left OPEN by malformed / truncated input (a missing
    // close marker — e.g. a `304 "LEADER_LINE{"` with no matching `305 "}"`
    // because the entity stream ended early). Without this, every partially
    // decoded frame still on the stack — and the data routed into it — is
    // silently dropped (the leader line / leader / context never reaches its
    // parent). Drain LIFO via the SAME `closeFrame` path a real close marker
    // would take, so the partial frame is attached to its parent exactly as if
    // the close had been present (byte-faithfulness, ПРИОРИТЕТ #1; never drop).
    while (this.stack.length > 0) {
      this.closeFrame();
    }
    return {
      hasContextMarkers: this.hasContext,
      contextData: this.contextData,
      leaderRoots: this.leaderRoots,
      secondaryContexts: this.secondaryContexts,
      rawContextGroups: this.rawContextGroups,
      annotativeContexts: this.annotativeContexts,
      blockAttributeReferences: this.blockAttributeReferences,
    };
  }

  // ── frame stack ──────────────────────────────────────────────────────────

  private openFrame(
    scope: "context" | "leader" | "leaderLine" | "nested",
  ): void {
    switch (scope) {
      case "context": {
        this.hasContext = true;
        this.contextCount += 1;
        // #294 r2 — start the context EMPTY: hasMText/hasBlock are set only
        // when the source actually carries group 290/296 (feedContext), so
        // `false` now means "source had 290/296 = 0" and `undefined` means
        // "source omitted the group". Pre-initialising them to false made the
        // two indistinguishable and the writer re-emitted invented `290 0` /
        // `296 0` on a raw-only CONTEXT_DATA block (absence-preserving).
        this.stack.push({
          kind: "context",
          ctx: {},
          points: new Map(),
          isPrimary: this.contextCount === 1,
          roots: [],
          rawGroups: [],
          annot: { scale: 1 },
        });
        break;
      }
      case "leader":
        this.stack.push({
          kind: "leader",
          node: { lines: [] },
          points: new Map(),
          breaks: [],
          rawGroups: [],
        });
        break;
      case "leaderLine":
        this.stack.push({
          kind: "leaderLine",
          line: { vertices: [] },
          breaks: [],
          rawGroups: [],
        });
        break;
      case "nested":
        this.stack.push({ kind: "nested" });
        break;
    }
  }

  /** Closes are LIFO: pop the current top frame and attach it to its parent. */
  private closeFrame(): void {
    const frame = this.stack.pop();
    if (frame === undefined) return;
    switch (frame.kind) {
      case "leaderLine":
        this.closeLeaderLine(frame);
        break;
      case "leader":
        this.closeLeader(frame);
        break;
      case "context":
        this.closeContext(frame);
        break;
      case "nested":
        break;
    }
  }

  private closeLeaderLine(frame: LeaderLineFrame): void {
    if (frame.breaks.length > 0) frame.line.breakPoints = frame.breaks;
    // The bridge schema requires every leader line to carry ≥1 vertex; drop an
    // empty line (matches the legacy buildMLeaderRoots guard). If a dropped line
    // carried unrecognised groups, they cannot ride a frame that will not exist —
    // fall back to the flat geom-level remainder so they still round-trip verbatim
    // (Codex #268; never drop — ПРИОРИТЕТ #1).
    if (frame.line.vertices.length === 0) {
      for (const g of frame.rawGroups) this.pushRawContextGroup(g);
      return;
    }
    // Codex #268 — attach the in-frame remainder so the writer re-emits it inside
    // the 304…305 frame (restored structural position) rather than flat at the end.
    if (frame.rawGroups.length > 0) frame.line.rawGroups = frame.rawGroups;
    const leader = this.nearestLeader();
    if (leader) leader.node.lines.push(frame.line);
  }

  private closeLeader(frame: LeaderFrame): void {
    if (frame.breaks.length > 0) frame.node.breakPoints = frame.breaks;
    const lastLeaderPoint = toPoint(frame.points, "lastLeaderPoint");
    if (lastLeaderPoint) frame.node.lastLeaderPoint = lastLeaderPoint;
    const doglegVector = toPoint(frame.points, "doglegVector");
    if (doglegVector) frame.node.doglegVector = doglegVector;
    const root: DxfMLeaderLeaderRoot = { nodes: [] };
    // A LEADER with no lines is dropped (no frame to host on export); its
    // unrecognised groups fall back to the flat geom-level remainder so they
    // still round-trip verbatim (Codex #268; never drop — ПРИОРИТЕТ #1).
    if (frame.node.lines.length === 0) {
      for (const g of frame.rawGroups) this.pushRawContextGroup(g);
      return;
    }
    // Codex #268 — attach the in-frame remainder so the writer re-emits it inside
    // the 302…303 frame (restored structural position) rather than flat at the end.
    if (frame.rawGroups.length > 0) frame.node.rawGroups = frame.rawGroups;
    root.nodes.push(frame.node);
    const ctx = this.nearestContext();
    if (ctx) ctx.roots.push(root);
  }

  private closeContext(frame: ContextFrame): void {
    const { ctx, points } = frame;
    const basePoint = toPoint(points, "basePoint");
    if (basePoint) ctx.basePoint = basePoint;
    const planeOrigin = toPoint(points, "planeOrigin");
    if (planeOrigin) ctx.planeOrigin = planeOrigin;
    const planeXAxis = toPoint(points, "planeXAxis");
    if (planeXAxis) ctx.planeXAxis = planeXAxis;
    const planeYAxis = toPoint(points, "planeYAxis");
    if (planeYAxis) ctx.planeYAxis = planeYAxis;
    if (frame.mtext) {
      const extrusion = toPoint(points, "mtextExtrusion");
      if (extrusion) frame.mtext.extrusion = extrusion;
      const insert = toPoint(points, "mtextInsert");
      if (insert) frame.mtext.insert = insert;
      const dir = toPoint(points, "mtextDirection");
      if (dir) frame.mtext.textDirection = dir;
      ctx.mTextData = frame.mtext;
    }
    if (frame.block) {
      const extrusion = toPoint(points, "blockExtrusion");
      if (extrusion) frame.block.extrusion = extrusion;
      const insert = toPoint(points, "blockInsert");
      if (insert) frame.block.insert = insert;
      const scale = toPoint(points, "blockScale");
      if (scale) frame.block.scale = scale;
      ctx.blockData = frame.block;
    }
    // One per-scale snapshot per CONTEXT_DATA (B2 — established behaviour).
    this.annotativeContexts.push(frame.annot);
    if (frame.isPrimary) {
      // First CONTEXT_DATA → the typed structural tree + leader hierarchy. Its
      // header-level raw groups feed the flat geom.rawContextGroups channel the
      // primary writer emits (Codex #285-9 — unchanged behaviour for the common
      // single-context MLEADER; order preserved via encounter-order buffering).
      this.contextData = ctx;
      this.leaderRoots = frame.roots;
      for (const g of frame.rawGroups) this.rawContextGroups.push(g);
    } else {
      // Secondary (annotative per-scale) CONTEXT_DATA — keep the FULL typed tree
      // so the writer re-emits one block per scale instead of dropping it
      // (Codex #268). Carry THIS block's own raw groups so the writer re-emits
      // them inside this secondary block rather than leaking them into the
      // primary (Codex #285-9).
      const entry: {
        context: DxfMLeaderContext;
        leaderRoots: DxfMLeaderLeaderRoot[];
        rawContextGroups?: DxfGroup[];
      } = { context: ctx, leaderRoots: frame.roots };
      if (frame.rawGroups.length > 0) entry.rawContextGroups = frame.rawGroups;
      this.secondaryContexts.push(entry);
    }
  }

  private nearestLeader(): LeaderFrame | undefined {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const f = this.stack[i];
      if (f && f.kind === "leader") return f;
    }
    return undefined;
  }

  private nearestContext(): ContextFrame | undefined {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const f = this.stack[i];
      if (f && f.kind === "context") return f;
    }
    return undefined;
  }

  // ── depth-0 body: trailing block-attribute linkage tuples ────────────────

  private feedBody(g: DxfGroup): void {
    // #432 r10 R10-2 — latch the AcDbMLeader BODY start so a block-attribute tuple
    // `330` is distinguished from the pre-subclass entity-owner `330`.
    if (g.code === 100 && g.value.trim() === "AcDbMLeader") {
      this.bodyStarted = true;
      return;
    }
    switch (g.code) {
      case 330:
        // Only a body-330 (after the AcDbMLeader marker) opens a real tuple; the
        // entity-owner 330 (pre-subclass) must NOT latch (R10-2). Without this a
        // stray 177/44/302 with no body-330 fabricated an attribute from the owner.
        if (!this.bodyStarted) break;
        // #432 r11 R11-2 — a NEW 330 FLUSHES the previous tuple first (ezdxf
        // `extract_block_attribs` stores per-330), so a tuple with no trailing 302
        // is preserved instead of being overwritten + lost.
        this.flushBlockAttr();
        this.baHandle = g.value.trim();
        this.baIndex = null;
        this.baWidth = null;
        this.baValue = null;
        break;
      case 177:
        if (this.baHandle !== null) this.baIndex = int(g);
        break;
      case 44:
        if (this.baHandle !== null) this.baWidth = num(g);
        break;
      case 302:
        // #432 r11 R11-2 — latch the text; the tuple flushes on the NEXT 330 or at
        // end-of-body (not here), matching ezdxf's per-330 store.
        if (this.baHandle !== null) this.baValue = decodeDxfUnicode(g.value); // T9 PR-3 decode
        break;
      default:
        break;
    }
  }

  /** #432 r11 R11-2 — push the pending block-attribute tuple (if a 330 opened one)
   * and reset. Called on a new 330 and at finalize, so a tuple with NO trailing
   * 302 (text='') still round-trips — matching ezdxf `extract_block_attribs`. */
  private flushBlockAttr(): void {
    if (this.baHandle === null) return;
    // #432 r11 (@codex) — only a REAL tuple (≥1 of 177/44/302 actually seen)
    // becomes a block-attribute reference. A BARE `330` opener with NO fields is a
    // malformed/vendor group, NOT a tuple — do NOT fabricate a `{index:0, width:0,
    // value:""}` attribute from it (the parser parks the bare 330 in extraGroups so
    // it round-trips verbatim). Coherent with the entityParser bare-opener gate.
    if (
      this.baIndex !== null ||
      this.baWidth !== null ||
      this.baValue !== null
    ) {
      this.blockAttributeReferences.push({
        attDefHandle: this.baHandle,
        index: this.baIndex ?? 0,
        width: this.baWidth ?? 0,
        value: this.baValue ?? "",
      });
    }
    this.baHandle = null;
    this.baIndex = null;
    this.baWidth = null;
    this.baValue = null;
  }

  // ── depth 1: MLeaderContext (header + inline MTextData/BlockData + trailer) ─

  private feedContext(frame: ContextFrame, g: DxfGroup): void {
    // Minimal per-scale snapshot (B2) reads its codes in parallel — it is a
    // separate simplified projection, it does not "consume" the group.
    this.feedAnnot(frame.annot, g);

    // 290/296 are the inline-presence flags: they gate creation of the
    // MTextData / BlockData objects (ezdxf has_mtext / has_block).
    if (g.code === 290) {
      frame.ctx.hasMText = bool(g);
      if (frame.ctx.hasMText && !frame.mtext) frame.mtext = {};
      return;
    }
    if (g.code === 296) {
      frame.ctx.hasBlock = bool(g);
      if (frame.ctx.hasBlock && !frame.block) frame.block = {};
      return;
    }

    // The context sub-sections use DISJOINT code sets, so routing by code
    // membership + object presence is unambiguous (header/MTextData/BlockData/
    // trailer share no group code). MTextData/BlockData fields only route when
    // the 290/296 flag created their object.
    if (frame.mtext && this.routeMText(frame, g)) return;
    if (frame.block && this.routeBlock(frame, g)) return;
    if (this.routeContextHeader(frame, g)) return;

    // Codex #285-9 — park unrecognised header groups WITH this context frame so
    // they re-emit in the SAME CONTEXT_DATA block (primary or secondary).
    frame.rawGroups.push(g);
  }

  private routeContextHeader(frame: ContextFrame, g: DxfGroup): boolean {
    const ctx = frame.ctx;
    switch (g.code) {
      case 40:
        ctx.scale = num(g);
        return true;
      case 10:
        setAxis(frame.points, "basePoint", "x", num(g));
        return true;
      case 20:
        setAxis(frame.points, "basePoint", "y", num(g));
        return true;
      case 30:
        setAxis(frame.points, "basePoint", "z", num(g));
        return true;
      case 41:
        ctx.charHeight = num(g);
        return true;
      case 140:
        ctx.arrowHeadSize = num(g);
        return true;
      case 145:
        ctx.landingGapSize = num(g);
        return true;
      case 174:
        ctx.textLeftAttachment = int(g);
        return true;
      case 175:
        ctx.textRightAttachment = int(g);
        return true;
      case 176:
        ctx.textAlignType = int(g);
        return true;
      case 177:
        ctx.attachmentType = int(g);
        return true;
      case 110:
        setAxis(frame.points, "planeOrigin", "x", num(g));
        return true;
      case 120:
        setAxis(frame.points, "planeOrigin", "y", num(g));
        return true;
      case 130:
        setAxis(frame.points, "planeOrigin", "z", num(g));
        return true;
      case 111:
        setAxis(frame.points, "planeXAxis", "x", num(g));
        return true;
      case 121:
        setAxis(frame.points, "planeXAxis", "y", num(g));
        return true;
      case 131:
        setAxis(frame.points, "planeXAxis", "z", num(g));
        return true;
      case 112:
        setAxis(frame.points, "planeYAxis", "x", num(g));
        return true;
      case 122:
        setAxis(frame.points, "planeYAxis", "y", num(g));
        return true;
      case 132:
        setAxis(frame.points, "planeYAxis", "z", num(g));
        return true;
      case 297:
        ctx.planeNormalReversed = bool(g);
        return true;
      case 272:
        ctx.topAttachment = int(g);
        return true;
      case 273:
        ctx.bottomAttachment = int(g);
        return true;
      default:
        return false;
    }
  }

  private routeMText(frame: ContextFrame, g: DxfGroup): boolean {
    const m = frame.mtext;
    if (!m) return false;
    switch (g.code) {
      case 304:
        m.defaultContent = decodeDxfUnicode(g.value); // T9 PR-3 — MTextData default content decode
        return true;
      case 11:
        setAxis(frame.points, "mtextExtrusion", "x", num(g));
        return true;
      case 21:
        setAxis(frame.points, "mtextExtrusion", "y", num(g));
        return true;
      case 31:
        setAxis(frame.points, "mtextExtrusion", "z", num(g));
        return true;
      case 340:
        m.styleHandle = g.value.trim();
        return true;
      case 12:
        setAxis(frame.points, "mtextInsert", "x", num(g));
        return true;
      case 22:
        setAxis(frame.points, "mtextInsert", "y", num(g));
        return true;
      case 32:
        setAxis(frame.points, "mtextInsert", "z", num(g));
        return true;
      case 13:
        setAxis(frame.points, "mtextDirection", "x", num(g));
        return true;
      case 23:
        setAxis(frame.points, "mtextDirection", "y", num(g));
        return true;
      case 33:
        setAxis(frame.points, "mtextDirection", "z", num(g));
        return true;
      case 42:
        m.rotation = num(g);
        return true;
      case 43:
        m.width = num(g);
        return true;
      case 44:
        m.definedHeight = num(g);
        return true;
      case 45:
        m.lineSpacingFactor = num(g);
        return true;
      case 170:
        m.lineSpacingStyle = int(g);
        return true;
      case 90:
        m.color = g.value.trim();
        return true;
      case 171:
        m.alignment = int(g);
        return true;
      case 172:
        m.flowDirection = int(g);
        return true;
      case 91:
        m.backgroundColor = g.value.trim();
        return true;
      case 141:
        m.backgroundScaleFactor = num(g);
        return true;
      case 92:
        m.backgroundTransparency = num(g);
        return true;
      case 291:
        m.useWindowBackground = bool(g);
        return true;
      case 292:
        m.hasBackgroundFill = bool(g);
        return true;
      case 173:
        m.columnType = int(g);
        return true;
      case 293:
        m.useAutoHeight = bool(g);
        return true;
      case 142:
        m.columnWidth = num(g);
        return true;
      case 143:
        m.columnGutter = num(g);
        return true;
      case 294:
        m.columnFlowReversed = bool(g);
        return true;
      case 144:
        (m.columnSizes ??= []).push(num(g));
        return true;
      case 295:
        m.useWordBreak = bool(g);
        return true;
      default:
        return false;
    }
  }

  private routeBlock(frame: ContextFrame, g: DxfGroup): boolean {
    const b = frame.block;
    if (!b) return false;
    switch (g.code) {
      case 341:
        b.blockRecordHandle = g.value.trim();
        return true;
      case 14:
        setAxis(frame.points, "blockExtrusion", "x", num(g));
        return true;
      case 24:
        setAxis(frame.points, "blockExtrusion", "y", num(g));
        return true;
      case 34:
        setAxis(frame.points, "blockExtrusion", "z", num(g));
        return true;
      case 15:
        setAxis(frame.points, "blockInsert", "x", num(g));
        return true;
      case 25:
        setAxis(frame.points, "blockInsert", "y", num(g));
        return true;
      case 35:
        setAxis(frame.points, "blockInsert", "z", num(g));
        return true;
      case 16:
        setAxis(frame.points, "blockScale", "x", num(g));
        return true;
      case 26:
        setAxis(frame.points, "blockScale", "y", num(g));
        return true;
      case 36:
        setAxis(frame.points, "blockScale", "z", num(g));
        return true;
      case 46:
        b.rotation = num(g);
        return true;
      case 93:
        b.color = g.value.trim();
        return true;
      case 47:
        (b.matrix ??= []).push(num(g));
        return true;
      default:
        return false;
    }
  }

  // ── depth 2: LeaderData ──────────────────────────────────────────────────

  private feedLeader(frame: LeaderFrame, g: DxfGroup): void {
    const node = frame.node;
    switch (g.code) {
      case 290:
        node.hasLastLeaderLine = bool(g);
        return;
      case 291:
        node.hasDoglegVector = bool(g);
        return;
      case 10:
        setAxis(frame.points, "lastLeaderPoint", "x", num(g));
        return;
      case 20:
        setAxis(frame.points, "lastLeaderPoint", "y", num(g));
        return;
      case 30:
        setAxis(frame.points, "lastLeaderPoint", "z", num(g));
        return;
      case 11:
        setAxis(frame.points, "doglegVector", "x", num(g));
        return;
      case 21:
        setAxis(frame.points, "doglegVector", "y", num(g));
        return;
      case 31:
        setAxis(frame.points, "doglegVector", "z", num(g));
        return;
      case 90:
        node.leaderIndex = int(g);
        return;
      case 40:
        node.doglegLength = num(g);
        return;
      case 271:
        node.attachmentDirection = int(g);
        return;
      // LeaderData breaks — alternating 12↔13 (start↔end), no index. NOTE the
      // default case below routes to the FRAME-LOCAL rawGroups (Codex #268), so
      // the `default:` of THIS switch is overridden further down.
      case 12:
        frame.breaks.push({
          start: { x: num(g), y: 0, z: 0 },
          end: { x: 0, y: 0, z: 0 },
        });
        return;
      case 22:
        this.setBreakAxis(frame.breaks, "start", "y", g);
        return;
      case 32:
        this.setBreakAxis(frame.breaks, "start", "z", g);
        return;
      case 13:
        this.setBreakAxis(frame.breaks, "end", "x", g);
        return;
      case 23:
        this.setBreakAxis(frame.breaks, "end", "y", g);
        return;
      case 33:
        this.setBreakAxis(frame.breaks, "end", "z", g);
        return;
      default:
        // Codex #268 — keep the unrecognised group WITH its LEADER frame so the
        // writer re-emits it inside 302…303 (frame association), not flat at the
        // end of CONTEXT_DATA.
        frame.rawGroups.push(g);
        return;
    }
  }

  // ── depth 3: LeaderLine ──────────────────────────────────────────────────

  private feedLeaderLine(frame: LeaderLineFrame, g: DxfGroup): void {
    const line = frame.line;
    switch (g.code) {
      case 10:
        line.vertices.push({ x: num(g), y: 0, z: 0 });
        return;
      case 20: {
        const v = line.vertices[line.vertices.length - 1];
        if (v) v.y = num(g);
        else this.pushRawContextGroup(g);
        return;
      }
      case 30: {
        const v = line.vertices[line.vertices.length - 1];
        if (v) v.z = num(g);
        else this.pushRawContextGroup(g);
        return;
      }
      // LeaderLine breaks — 90 break_index + 11/21/31 start + 12/22/32 end.
      case 90:
        frame.breaks.push({
          start: { x: 0, y: 0, z: 0 },
          end: { x: 0, y: 0, z: 0 },
          breakIndex: int(g),
        });
        return;
      case 11:
        this.setBreakAxis(frame.breaks, "start", "x", g);
        return;
      case 21:
        this.setBreakAxis(frame.breaks, "start", "y", g);
        return;
      case 31:
        this.setBreakAxis(frame.breaks, "start", "z", g);
        return;
      case 12:
        this.setBreakAxis(frame.breaks, "end", "x", g);
        return;
      case 22:
        this.setBreakAxis(frame.breaks, "end", "y", g);
        return;
      case 32:
        this.setBreakAxis(frame.breaks, "end", "z", g);
        return;
      case 91:
        line.leaderLineIndex = int(g);
        return;
      case 92:
        line.colorOverride = g.value.trim();
        return;
      case 340:
        line.lineTypeHandle = g.value.trim();
        return;
      case 170:
        line.lineType = int(g);
        return;
      case 171:
        line.lineWeightOverride = g.value.trim();
        return;
      default:
        // Codex #268 — keep the unrecognised group WITH its LEADER_LINE frame so
        // the writer re-emits it inside 304…305 (frame association), not flat at
        // the end of CONTEXT_DATA.
        frame.rawGroups.push(g);
        return;
    }
  }

  private setBreakAxis(
    breaks: DxfMLeaderBreakPoint[],
    end: "start" | "end",
    axis: "x" | "y" | "z",
    g: DxfGroup,
  ): void {
    const b = lastBreak(breaks);
    if (b) b[end][axis] = num(g);
    else this.pushRawContextGroup(g);
  }

  // ── per-scale snapshot (B2) ──────────────────────────────────────────────

  /**
   * Minimal per-scale CONTEXT_DATA snapshot. Field↔code map corrected per the
   * §7 code map (gap B): 145 = landing_gap_size → landingGap (was doglegLength);
   * 42 = MTextData rotation → textRotation; 43 = MTextData width → textWidth
   * (the two were swapped); dead 146 dropped. 40/41/140/90 were already
   * correct. doglegLength has no context-header source (it is a LeaderData
   * depth-2 field) and is left to the bridge default.
   */
  private feedAnnot(annot: DxfMLeaderAnnotativeContext, g: DxfGroup): void {
    switch (g.code) {
      case 40:
        annot.scale = num(g);
        break;
      case 41:
        annot.textHeight = num(g);
        break;
      case 140:
        annot.arrowheadSize = num(g);
        break;
      case 145:
        annot.landingGap = num(g);
        break;
      case 90:
        annot.textColor = g.value.trim();
        break;
      case 42:
        annot.textRotation = num(g);
        break;
      case 43:
        annot.textWidth = num(g);
        break;
      default:
        break;
    }
  }
}
